

/** @jsxImportSource react */
import React, { useEffect, useState } from 'react'
import { useNavigate, NavLink, useLocation } from 'react-router-dom'
import { useAuth } from '../contexts/auth'
import { UserRole } from '../types'
import {
  LayoutDashboard,
  Package,
  FileBarChart,
  Settings,
  LogOut,
  Users,
  Bell,
  Calculator,
  Inbox,
  Send,
  ChevronLeft,
  ChevronRight,
  X,
  User,
  Search,
  ListChecks,
} from 'lucide-react'
import { useTranslation } from '../i18n'

const NavItem = ({ to, icon, label, isCollapsed, onClick }) => {
  const location = useLocation()
  const isActive =
    location.pathname === to || location.pathname.startsWith(`${to}/`)
  return (
    <NavLink
      to={to}
      className={`
        flex items-center p-3 my-1 rounded-lg transition-all duration-200
        ${isActive ? 'bg-gradient-to-r from-primary-50 to-primary-100/70 text-primary-600 font-medium shadow-sm' : 'text-secondary-600 hover:bg-background-off hover:text-primary-600'}
        ${isCollapsed ? 'justify-center' : ''}
      `}
      onClick={onClick}
    >
      <div
        className={`
          ${isCollapsed ? 'w-6 h-6' : 'w-5 h-5'} 
          transition-all duration-200
          ${isActive ? 'text-primary-600' : 'text-secondary-500'}
        `}
      >
        {icon}
      </div>
      {!isCollapsed && (
        <span className="ms-3 font-medium transition-all duration-200 whitespace-nowrap overflow-hidden text-ellipsis">
          {label}
        </span>
      )}
    </NavLink>
  )
}
// Section divider component
const SectionDivider = ({ title, isCollapsed }) => {
  if (isCollapsed) {
    return <div className="h-px bg-secondary-200 my-3 mx-2"></div>
  }
  return (
    <div className="flex items-center gap-2 px-3 my-3">
      <p className="text-xs font-semibold text-secondary-400 uppercase tracking-wider whitespace-nowrap">
        {title}
      </p>
      <div className="flex-1 h-px bg-secondary-200"></div>
    </div>
  )
}
const Sidebar = ({ isMobileOpen, onCloseMobile, isDesktopCollapsed, setIsDesktopCollapsed }) => {
  const { user, logout } = useAuth()
  const { t, dir } = useTranslation()
  const navigate = useNavigate()
  const [showMobileOverlay, setShowMobileOverlay] = useState(false)
  
  // Handle mobile overlay animation
  useEffect(() => {
    if (isMobileOpen) {
      setShowMobileOverlay(true)
    } else {
      const timer = setTimeout(() => {
        setShowMobileOverlay(false)
      }, 300) // Match transition duration
      return () => clearTimeout(timer)
    }
  }, [isMobileOpen])

  if (!user) return null

  const handleLogout = () => {
    logout()
    navigate('/')
  }

  const isRTL = dir === 'rtl'
  const handleNavItemClick = () => {
    if (window.innerWidth < 768) {
      onCloseMobile()
    }
  }

  return (
    <>
      {/* Mobile overlay */}
      {showMobileOverlay && (
        <div
          className="fixed inset-0 bg-secondary-900/50 backdrop-blur-sm z-20 md:hidden
            transition-opacity duration-300 ease-in-out
            opacity-100"
          onClick={onCloseMobile}
        />
      )}
      {/* Sidebar */}
      <aside
        className={`
          flex flex-col h-screen z-30 transition-all duration-300 ease-in-out 
          fixed md:sticky top-0 md:top-0
          bg-white border-e border-secondary-100 shadow-md md:shadow-sm
          ${isMobileOpen ? 'translate-x-0 rtl:-translate-x-0' : '-translate-x-full rtl:translate-x-full'}
          ${isDesktopCollapsed ? 'md:w-20' : 'md:w-64'}
          md:translate-x-0 rtl:md:translate-x-0 w-[280px] md:w-auto
        `}
      >
        {/* Header */}
        <div className="flex items-center p-4 h-16 border-b border-secondary-100 bg-gradient-to-r from-white to-secondary-50/50 shrink-0">
          <div
            className={`flex items-center ${isDesktopCollapsed ? 'justify-center w-full' : 'justify-between w-full'}`}
          >
            {!isDesktopCollapsed && (
              <div className="text-xl font-bold text-primary-600 tracking-tight truncate">
                {t('tagExpress')}
              </div>
            )}
            {isDesktopCollapsed && (
              <div className="text-xl font-bold text-primary-600">TE</div>
            )}
            <div className="flex items-center">
              <button
                onClick={onCloseMobile}
                className="p-2 rounded-lg md:hidden text-secondary-500 hover:text-primary-600 transition-colors"
                aria-label={t('close')}
              >
                <X size={20} />
              </button>
              <button
                onClick={() => setIsDesktopCollapsed(!isDesktopCollapsed)}
                className="p-2 rounded-lg hidden md:flex items-center justify-center hover:bg-background-off text-secondary-500 hover:text-primary-600 transition-colors"
                aria-label={isDesktopCollapsed ? t('expandSidebar') : t('collapseSidebar')}
              >
                {isDesktopCollapsed ? (
                  isRTL ? (
                    <ChevronLeft size={20} />
                  ) : (
                    <ChevronRight size={20} />
                  )
                ) : isRTL ? (
                  <ChevronRight size={20} />
                ) : (
                  <ChevronLeft size={20} />
                )}
              </button>
            </div>
          </div>
        </div>
        {/* User profile section */}
        {!isDesktopCollapsed && (
          <div className="px-4 py-3 border-b border-secondary-100 shrink-0">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 overflow-hidden">
                    <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center text-primary-600 font-semibold text-lg flex-shrink-0">
                        {user.name?.charAt(0) || 'U'}
                    </div>
                    <div className="overflow-hidden">
                        <h3 className="font-medium text-secondary-800 truncate">{user.name}</h3>
                        <p className="text-xs text-secondary-500 truncate">{user.email}</p>
                    </div>
                </div>
                <button 
                    onClick={handleLogout} 
                    className="p-2 rounded-full text-rose-500 hover:bg-rose-50 hover:text-rose-700 transition-colors flex-shrink-0"
                    title={t('logout')}
                >
                    <LogOut size={18} />
                </button>
            </div>
          </div>
        )}
        {/* Navigation - with flex-1 and overflow handling */}
        <nav className="flex-1 overflow-y-auto scrollbar-thin p-3 min-h-0">
          <div>
            <SectionDivider
              title={t('sidebar_main')}
              isCollapsed={isDesktopCollapsed}
            />
            <NavItem
              to="/dashboard"
              icon={<LayoutDashboard className="w-full h-full" />}
              label={t('dashboard')}
              isCollapsed={isDesktopCollapsed}
              onClick={handleNavItemClick}
            />
            <NavItem
              to="/track"
              icon={<Search className="w-full h-full" />}
              label={t('trackShipment')}
              isCollapsed={isDesktopCollapsed}
              onClick={handleNavItemClick}
            />
            <NavItem
              to="/orders"
              icon={<Package className="w-full h-full" />}
              label={t('orders')}
              isCollapsed={isDesktopCollapsed}
              onClick={handleNavItemClick}
            />
             <NavItem
                to="/notifications"
                icon={<Bell className="w-full h-full" />}
                label={t('notifications')}
                isCollapsed={isDesktopCollapsed}
                onClick={handleNavItemClick}
              />
            <NavItem
              to="/profile"
              icon={<User className="w-full h-full" />}
              label={t('profile')}
              isCollapsed={isDesktopCollapsed}
              onClick={handleNavItemClick}
            />
            {user.role === UserRole.Client && (
              <NavItem
                to="/shipping/new"
                icon={<Send className="w-full h-full" />}
                label={t('requestShipping')}
                isCollapsed={isDesktopCollapsed}
                onClick={handleNavItemClick}
              />
            )}
            {user.role === UserRole.Admin && (
              <NavItem
                to="/incoming-orders"
                icon={<Inbox className="w-full h-full" />}
                label={t('incomingOrders')}
                isCollapsed={isDesktopCollapsed}
                onClick={handleNavItemClick}
              />
            )}
            {user.role === UserRole.Staff && (
              <NavItem
                to="/price-check-list"
                icon={<ListChecks className="w-full h-full" />}
                label={t('priceCheckList')}
                isCollapsed={isDesktopCollapsed}
                onClick={handleNavItemClick}
              />
            )}
          </div>
          {user.role === UserRole.Admin && (
            <div className="mt-2">
              <SectionDivider
                title={t('sidebar_admin')}
                isCollapsed={isDesktopCollapsed}
              />
              <NavItem
                to="/reports"
                icon={<FileBarChart className="w-full h-full" />}
                label={t('reports')}
                isCollapsed={isDesktopCollapsed}
                onClick={handleNavItemClick}
              />
              <NavItem
                to="/accountant-report"
                icon={<Calculator className="w-full h-full" />}
                label={t('accountantReport')}
                isCollapsed={isDesktopCollapsed}
                onClick={handleNavItemClick}
              />
              <NavItem
                to="/users"
                icon={<Users className="w-full h-full" />}
                label={t('userManagement')}
                isCollapsed={isDesktopCollapsed}
                onClick={handleNavItemClick}
              />
              <NavItem
                to="/notification-preferences"
                icon={<Bell className="w-full h-full" />}
                label={t('notificationPreferences')}
                isCollapsed={isDesktopCollapsed}
                onClick={handleNavItemClick}
              />
              <NavItem
                to="/settings"
                icon={<Settings className="w-full h-full" />}
                label={t('settings')}
                isCollapsed={isDesktopCollapsed}
                onClick={handleNavItemClick}
              />
            </div>
          )}
        </nav>
      </aside>
    </>
  )
}
export default Sidebar